import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { Button, Text, H4, H3, H2, Dropdown, Panel, Table, Small, Collapse, Badge } from '@bigcommerce/big-design';
import { ArrowDropDownIcon, FileDownloadIcon, ChevronLeftIcon, ChevronRightIcon } from '@bigcommerce/big-design-icons';

import LoadingPlaceholder from '../partials/LoadingPlaceholder';
import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import { indexFulfillmentStatuses } from '../../redux/actions/pickOrder';
import { getIncompleteOrders } from '../../redux/actions/incompleteOrders';

import { indexBarcodeTemplates } from '../../redux/actions/barcodeTemplates';
import { requestOrderPrint } from '../../redux/actions/prints';
import { locationsIndex } from '../../redux/actions/locations';

const OrderLabelPrint = ({ payload, getTemplates, barcodeTemplates, stores, postPrint, pickOrder, downloadUrl, prints, missingBarcodes, settings, getCustomFields, customFields, locations, loadLocations, orderStatuses, incompleteOrders, loadFulfillmentStatuses, searchIncompleteOrders }) => {
  const [submitted, setSubmitted] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState({});

  const [orderPage, setOrderPage] = useState(1);

  const switchActiveTemplate = (event) => {
    let template = barcodeTemplates.templates.find(template => template.uuid === event.hash);
    setActiveTemplate(template);
  }

  const templates = () => {
    let templateItems = [];

    barcodeTemplates.templates.map(template => 
      templateItems.push(
        {
          content: template.template_name,
          onItemClick: (event) => switchActiveTemplate(event),
          hash: template.uuid
        }
      )
    )

    return templateItems;
  }

  const refreshOrders = () => {
    searchIncompleteOrders(payload, orderPage, false);
  }

  const setIncompleteOrdersPage = (page) => {
    setOrderPage(page);
    searchIncompleteOrders(payload, page, false);
  }

  const download = (orderId) => {
    postPrint(payload, activeTemplate.uuid, orderId);
    setSubmitted(true);
  }

  useEffect(() => {
    if(downloadUrl && submitted) { 
      setTimeout(() => {
        let win = window.open(downloadUrl, '_blank');
        win.focus();
      }, 0);

      setSubmitted(false);
    }
  }, [downloadUrl]);

  useEffect(() => {
    if (barcodeTemplates.status === 'success' && barcodeTemplates.templates.length > 0 && !activeTemplate.uuid) {
      setActiveTemplate(barcodeTemplates.templates[0]);
    }
  }, [barcodeTemplates]);

  useEffect(() => {
    searchIncompleteOrders(payload, orderPage, false);
    loadFulfillmentStatuses(payload);
    getTemplates(payload);
  }, []);

  const description = () => {
    return(
      <>
        <Badge label="Beta" variant="warning" />
        <Text>Select a template to print the selected barcodes with by choosing it from the Templates dropdown.</Text>
        <Text>Look through and download labels for an unfulfilled order below by clicking the "Download Labels" button.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  return(
    <>
      <PageHeader stripeBackground='#f9f2e2' helpLink="bigcommerce/print-labels-from-unfulfilled-orders/" />
      <SettingsPage title="Print Labels for Orders" description={description()}>
        <Panel>
          <H2>Print Template</H2>
          { barcodeTemplates.status === 'success' && barcodeTemplates.templates.length > 0 && 
            <Dropdown
              items={templates()}
              maxHeight={250}
              placement="bottom-start"
              toggle={<Button iconRight={<ArrowDropDownIcon />}>{activeTemplate?.template_name || "Select Template"}</Button>}
            />
          }
          { barcodeTemplates.status === 'success' && barcodeTemplates.templates.length === 0 && 
            <NoLabelTemplatesBox/>
          }

          { barcodeTemplates.status !== 'success' &&
            <LoadingSpinner size="xxSmall" statusText="Loading Templates" />
          }
        </Panel>

        <Panel>
          <H2>Unfulfilled Orders</H2>
          <div className="flex-container">
            <div className="flex-1">
              { incompleteOrders.isFetching &&
                <LoadingSpinner
                  statusText='Loading unfulfilled orders'
                />
              }

              { !incompleteOrders.isFetching && incompleteOrders.status === 'success' &&
                <>
                  <div className="flex-container top-15">
                    <div className="flex-1">
                      <Button
                        onClick={() => refreshOrders()}
                        disabled={pickOrder.isFetching}
                      >
                        Refresh Orders
                      </Button>
                    </div>

                    <div className="flex-container flex-1 flex-end">
                      <Button
                        iconLeft={<ChevronLeftIcon />}
                        variant="secondary"
                        disabled={!incompleteOrders.ordersPagination.has_previous_page}
                        onClick={() => setIncompleteOrdersPage(orderPage - 1)}
                      >
                        Previous Page
                      </Button>

                      <Button
                        iconRight={<ChevronRightIcon />}
                        variant="secondary"
                        disabled={!incompleteOrders.ordersPagination.has_theoretical_next}
                        onClick={() => setIncompleteOrdersPage(orderPage + 1)}
                      >
                        Next Page
                      </Button>

                    </div>
                  </div>

                  <Panel>
                    { 
                      incompleteOrders.orders.map((order) =>
                        <>
                          <div className="has-hover sku-line flex-container">
                            <div className="flex-4">
                              <H4>
                                <span className="no-style">
                                  <a target="_blank" href={`${stores.settings.control_panel_base_url}/manage/orders/${order.id}`}>Order #{order.id}</a>
                                </span>
                              </H4>
                               <Small>
                                { order.customer_message &&
                                 <>Customer Message: {order.customer_message}<br/></>
                                }
                                { order.staff_notes &&
                                  <>Staff Notes: {order.staff_notes}</>
                                }
                              </Small>
                            </div>

                            <div className="flex-1">
                              <Button
                                onClick={() => download(order.id)}
                                isLoading={submitted}
                                disabled={submitted || barcodeTemplates.status !== 'success' || barcodeTemplates.templates.length === 0}
                              >
                                Download Labels
                              </Button>
                            </div>
                          </div>
                        </>
                      )
                    }
                    { incompleteOrders.orders.length === 0 && orderPage === 1 &&
                      <Box
                        backgroundColor="primary20"
                        border="box"
                        borderRadius="normal"
                        padding="medium"
                      >
                        <H4>No Unfulfilled Orders</H4>
                        <Text>
                          You're all caught up! When you have orders in the "{pickOrder?.orderStatuses[11]?.name}" state, they will appear in a list here.
                        </Text>
                      </Box>
                    }
                    { incompleteOrders.orders.length === 0 && orderPage > 1 &&
                      <Box
                        backgroundColor="warning20"
                        border="box"
                        borderRadius="normal"
                        padding="medium"
                      >
                        <H4>No More Orders</H4>
                        <Text>
                          Click the "Previous Page" button to see unfulfilled orders.
                        </Text>
                      </Box>
                    }
                  </Panel>
                </>
              }
            </div>
          </div>
        </Panel>
      </SettingsPage>
    </>
  )

}


const mapStateToProps = state => ({
  stores: state.stores,
  barcodeTemplates: state.barcodeTemplates,
  prints: state.prints,
  downloadUrl: state.prints.downloadUrl,
  settings: state.settings,
  pickOrder: state.pickOrder,
  orderStatuses: state.pickOrder.orderStatuses,
  incompleteOrders: state.incompleteOrders,
});

const mapDispatchToProps = (dispatch) => ({
  loadFulfillmentStatuses: (payload) => dispatch(indexFulfillmentStatuses(payload)),
  searchIncompleteOrders: (payload, orderPage, searchSuspendedOrders) => dispatch(getIncompleteOrders(payload, orderPage, searchSuspendedOrders)),
  getTemplates: (payload) => dispatch(indexBarcodeTemplates(payload)),
  postPrint: (payload, template, orderId) => dispatch(requestOrderPrint(payload, template, orderId)),
  getCustomFields: (payload, productIds) => dispatch(productCustomFields(payload, productIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderLabelPrint);